import Vue from "vue";
import {
    fetchAppDetail,
    updateAppDetailApi,
    uploadPhoto,
    updateAppDetail,
    registerApp,
    deleteApp,
    cancelUpdate
} from "@/api";
import router from "@/router/index";
import { DEVELOPER_PROJECT_DETAIL } from "@/router/path";
import i18n from "@/i18n";

const regardlessLocale = ["categories", "locales", "defaultLocale"];

export const state = {
    appDetail: {},
    temp: {},
    selectedLocale: {}
};

export const getters = {
    appName: ({ appDetail: { clientName } }) => clientName,
    appId: ({ appDetail: { id } }) => id,
    appStatus: ({ appDetail: { status } }) => status,
    appDetail: ({ appDetail }) => appDetail,
    temp: ({ temp }) => temp,
    appDetailModified: ({ temp }) => !!Object.keys(temp).length,
    selectedLocale: ({ selectedLocale }) => selectedLocale,
    selectedLocalization: ({ temp, appDetail, selectedLocale }) => {
        const localizations = temp.localizations ?? appDetail.localizations;
        return (
            localizations?.find(
                localization => localization.locale === selectedLocale.key
            ) ?? {}
        );
    }
};

export const actions = {
    async FETCH_APP_DETAIL({ commit }, appId) {
        try {
            const { data } = await fetchAppDetail(appId);
            commit("SET_APP_DETAIL", data);
        } catch (e) {
            //
        }
    },
    CONFIRM_REISSUE_CLIENT_SECRET({ dispatch }) {
        dispatch("UPDATE_DIALOG", {
            title: i18n.t("reissueClientSecret.title"),
            content: i18n.t("reissueClientSecret.content"),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                { async_action: "confirm", label: i18n.t("label.confirm") }
            ],
            confirm: dialog => dialog.close()
        });
    },
    async SAVE_APP_DETAIL_API({
        commit,
        dispatch,
        state: {
            temp: { redirectUri },
            appDetail: { id }
        }
    }) {
        if (!redirectUri) return;
        redirectUri = redirectUri.filter(value => value);
        if (!redirectUri.length) {
            return dispatch("UPDATE_DIALOG", {
                ...i18n.t("appDetail.api.emptyRedirectUri"),
                actions: [{ action: "confirm", label: i18n.t("label.confirm") }]
            });
        }
        try {
            const { data } = await updateAppDetailApi(id, { redirectUri });
            commit("UPDATE_APP_DETAIL", data);
            commit("RESET_TEMP_APP_DETAIL");
            dispatch("UPDATE_SNACKBAR", {
                message: i18n.t("appDetail.api.saveRedirectUri")
            });
        } catch (e) {
            //
        }
    },
    async CANCEL_APP_DETAIL({
        commit,
        getters,
        dispatch,
        rootState: { route: { params: { tab } } }
    }) {
        if (!getters.appDetailModified) return true;
        const action = await dispatch("UPDATE_DIALOG", {
            ...i18n.t(
                tab
                    ? `appDetail.${
                        tab === "api"
                            ? "api.cancelRedirectUri"
                            : "information.cancelInformation"
                    }`
                    : "appRegister.cancelRegister"
            ),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                { action: "confirm", label: i18n.t("label.confirm") }
            ]
        });
        if (action === "confirm") {
            commit("RESET_TEMP_APP_DETAIL");
            commit("SET_TEMP_APP_LOCALIZATIONS");
            return true;
        }
        return false;
    },
    async UPLOAD_ICON_IMAGE(
        { state: { temp, appDetail }, commit },
        { key, file }
    ) {
        try {
            const { id: appId } = appDetail;
            const formData = new FormData();
            formData.append("imageFileName", file.name);
            formData.append("image", file);
            const url = await uploadPhoto(appId, formData);
            let value = url;
            if (key === "serviceImageUrl") {
                const serviceImageUrl =
                    temp.serviceImageUrl || appDetail.serviceImageUrl;
                value = serviceImageUrl ? [...serviceImageUrl, url] : [url];
            }
            commit("PATCH_TEMP_APP_DETAIL", { key, value });
        } catch ({ message }) {
            //
        }
    },
    async SAVE_APP_DETAIL_INFO({
        commit,
        getters,
        state: {
            temp,
            appDetail: { id: appId }
        },
        dispatch
    }) {
        if (!getters.appDetailModified) return;
        const action = await dispatch("UPDATE_DIALOG", {
            ...i18n.t("appDetail.information.saveInformation"),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                { action: "confirm", label: i18n.t("label.confirm") }
            ]
        });
        if (action === "confirm") {
            try {
                const { categories, defaultLocale, localizations } = temp;
                const localeKeys = temp.locales.map(locale => locale.key);

                const { data } = await updateAppDetail(appId, {
                    categories,
                    defaultLocale,
                    localizations: localizations.filter(localization =>
                        localeKeys.includes(localization.locale))
                });
                commit("SET_APP_DETAIL", data);
                if (temp.clientName) dispatch("FETCH_APPS");
                commit("RESET_TEMP_APP_DETAIL");
                commit("SET_TEMP_APP_LOCALIZATIONS");
                return true;
            } catch (e) {
                dispatch("UPDATE_ERROR_SNACKBAR", e);
            }
        }
        return false;
    },
    async REGISTER_APP({ state: { temp }, commit, dispatch }) {
        const action = await dispatch("UPDATE_DIALOG", {
            ...i18n.t("appRegister.saveRegister"),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                { action: "confirm", label: i18n.t("label.confirm") }
            ]
        });
        if (action === "confirm") {
            try {
                const { data } = await registerApp({
                    ...temp,
                    type: "WEB_APP"
                });
                commit("RESET_TEMP_APP_DETAIL");
                router.replace(
                    `${DEVELOPER_PROJECT_DETAIL}/${data.project.id}/apps`
                );
            } catch (e) {
                dispatch("UPDATE_ERROR_SNACKBAR", e);
            }
        }
    },
    async DELETE_APP({
        state: { appDetail: { id: appId } },
        commit,
        dispatch,
        rootState: { route: { params: { pId: projectId } } }
    }) {
        const action = await dispatch("UPDATE_DIALOG", {
            ...i18n.t("appDetail.deleteApplication"),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                { action: "confirm", label: i18n.t("label.confirm") }
            ]
        });
        if (action === "confirm") {
            try {
                await deleteApp(appId);
                commit("RESET_TEMP_APP_DETAIL");
                router.replace(`${DEVELOPER_PROJECT_DETAIL}/${projectId}/apps`);
            } catch (e) {
                //
            }
        }
    },
    async CANCEL_APP_UPDATE({
        state: { appDetail: { id: appId } },
        commit
    }) {
        try {
            const { data } = await cancelUpdate(appId);
            commit("SET_APP_DETAIL", data);
            commit("SET_TEMP_APP_LOCALIZATIONS");
        } catch (e) {
            //
        }
    }
};

export const mutations = {
    SET_APP_DETAIL(state, value) {
        state.appDetail = value;
    },
    UPDATE_APP_DETAIL(state, value) {
        state.appDetail = {
            ...state.appDetail,
            ...value
        };
    },
    RESET_APP_DETAIL(state) {
        state.appDetail = {};
        state.temp = {};
    },
    PATCH_TEMP_APP_DETAIL({ temp, selectedLocale }, { key, value }) {
        if (!regardlessLocale.includes(key) && temp.localizations) {
            const nextLocalizations = [...temp.localizations]?.map(
                localization => {
                    if (localization.locale === selectedLocale.key) {
                        return {
                            ...localization,
                            [key]: value
                        };
                    }
                    return localization;
                }
            );
            Vue.set(temp, "localizations", nextLocalizations);
        }
        Vue.set(temp, key, value);
    },
    SET_TEMP_APP_LOCALIZATIONS(state) {
        const { temp, appDetail, selectedLocale } = state;
        const localizations = temp.localizations ?? appDetail.localizations;
        const defaultLocale = temp.defaultLocale ?? appDetail.defaultLocale;
        const localization = localizations?.find(
            localization => localization.locale === selectedLocale.key
        );
        const defaultLocalization = localizations?.find(
            localization => localization.locale === defaultLocale
        );
        const newLocalization = {
            clientName: "",
            description: "",
            explanation: "",
            iconUrl: "",
            serviceImageUrl: [],
            serviceUrl: "",
            serviceInfoUrl: "",
            servicePrice: appDetail.servicePrice,
            servicePriceDesc: "",
            companyUrl: "",
            version: "",
            ...defaultLocalization,
            locale: selectedLocale.key
        };
        const nextLocalizations = localization
            ? localizations
            : [...localizations, newLocalization];
        const nextLocalization = {
            ...newLocalization,
            ...localization
        };
        state.temp = {
            categories: temp.categories ?? appDetail.categories,
            locales: temp.locales,
            defaultLocale: temp.defaultLocale ?? appDetail.defaultLocale,
            ...nextLocalization,
            localizations: nextLocalizations
        };
    },
    RESET_TEMP_APP_DETAIL(state) {
        state.temp = {};
    },
    SET_SELECTED_LOCALE(state, value) {
        state.selectedLocale = value;
    }
};
